import type { CotProperty, ObjectId, PropertyRef, Ref } from '@/api/types'

export const EMPTY_REF: Ref = { _id: null, code: null, display: '—' }

function nameOf(p: CotProperty): { code: string | null; display: string | null } {
  const n = p.name
  if (typeof n === 'string') return { code: null, display: n }
  if (n && typeof n === 'object') {
    return { code: n.code ?? null, display: n.display ?? null }
  }
  return { code: null, display: null }
}

/**
 * Normaliza una referencia a propiedad.
 *
 * El endpoint v2 devuelve las extensiones ya resueltas (corrección §2.2), pero
 * el de tarea individual v1 entrega ObjectIds pelados. Acá se aceptan las dos
 * formas: si viene resuelta se usa, y si viene como id se busca en el mapa de
 * propiedades ya cargadas.
 */
export function toRef(
  value: PropertyRef,
  resolved?: Map<ObjectId, CotProperty>,
): Ref {
  if (!value) return EMPTY_REF

  if (typeof value === 'string') {
    const hit = resolved?.get(value)
    if (!hit) return { _id: value, code: null, display: '—' }
    const { code, display } = nameOf(hit)
    return { _id: value, code, display: display ?? '—' }
  }

  const { code, display } = nameOf(value)
  return { _id: value._id ?? null, code, display: display ?? '—' }
}

/** Devuelve el id de una referencia venga como venga, para pedirla en lote. */
export function refId(value: PropertyRef): ObjectId | null {
  if (!value) return null
  if (typeof value === 'string') return value
  return value._id ?? null
}

/** Ids que todavía no están resueltos y hay que ir a buscar. */
export function unresolvedIds(
  values: PropertyRef[],
  resolved: Map<ObjectId, CotProperty>,
): ObjectId[] {
  const out = new Set<ObjectId>()
  for (const v of values) {
    if (typeof v === 'string' && !resolved.has(v)) out.add(v)
  }
  return [...out]
}
